import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { LoginPage, CustomBrandLogo } from "@ippon/shared-auth";
import { useDispatch, useSelector } from "react-redux";
import { login as loginThunk } from "@/store/authSlice";
import { consumePostLoginRedirect } from "@/utils/authUtils";
import { createPostLoginRestoreState } from "@/utils/postLoginRedirect";
import type { AppDispatch, RootState } from "@/store";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { SIGN_IN_FAILED_MESSAGE, consumeLogoutReason, resolveSignInErrorMessage } from "@/constants/clientAccess";
import "./Login.css";

const Login: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const loading = useSelector(
    (state: RootState) => state.auth.loginState.loading,
  );
  // 契約前・契約終了で強制ログアウトされたときの理由（管理画面化 SPEC §7.4）。表示は 1 回だけ
  const [logoutNotice] = useState(() => consumeLogoutReason());

  // パスワード忘れフローは設定画面(ippon-client-setting)FE へ委譲。
  // service_id を付けて渡し、完了後に設定画面から物件管理ログインへ戻せるようにする。
  const settingFeUrl = import.meta.env.VITE_SETTING_FE_URL ?? "";
  const serviceId = import.meta.env.VITE_SERVICE_ID ?? "";
  const forgotPasswordTo = settingFeUrl
    ? `${settingFeUrl}/checkmail?service_id=${encodeURIComponent(serviceId)}`
    : "/checkmail";

  return (
    <>
      <Box className="loginHeader">
        <Box className="loginHeaderContent">
          <a
            href="https://sales-brokerage.ippon-cloud.com/login"
            target="_blank"
            rel="noopener noreferrer"
            className="serviceLink"
          >
            売買仲介にログイン
            <OpenInNewIcon sx={{ fontSize: 16, marginLeft: 0.5 }} />
          </a>
        </Box>
      </Box>
      {logoutNotice && (
        <Box sx={{ maxWidth: 480, mx: "auto", mt: 3, px: 2 }}>
          <Typography role="alert" sx={{ fontSize: "14px", color: "error.main", textAlign: "center" }}>
            {logoutNotice}
          </Typography>
        </Box>
      )}
      <Box>
        <LoginPage
          renderLogo={<CustomBrandLogo label="物件管理" />}
          loading={loading}
          defaultRedirectPath="/home"
          // ログアウト/失効/未認証アクセス前にいた画面へ戻す (無ければ /home)
          getRedirectPath={consumePostLoginRedirect}
          getRedirectState={createPostLoginRestoreState}
          forgotPasswordTo={forgotPasswordTo}
          onSubmit={async ({ username, password, recaptchaToken }) => {
            try {
              // reCAPTCHA トークンは LoginPage(shared-auth) が送信直前に取得して渡す。
              const action = (await dispatch(
                loginThunk({ username, password, recaptchaToken }),
              )) as any;

              const status = action?.payload?.data?.status;
              if (status === 200) {
                // Tokens (IdToken/RefreshToken) are stored via slice on fulfilled
                return { ok: true };
              }

              // 403(reCAPTCHA不合格)・429(ロックアウト/レート制限)は BE の個別メッセージ。
              // 401(認証失敗・契約前・契約終了)は「ログインに失敗しました」
              return { ok: false, message: resolveSignInErrorMessage(action?.payload) };
            } catch (e: any) {
              return { ok: false, message: SIGN_IN_FAILED_MESSAGE };
            }
          }}
        />
      </Box>
    </>
  );
};

export default Login;
